import { buildBrandedQr, loadBrandLogo, QR_LOGO_SCALE_DEFAULT } from '@/lib/brandedQr'
import type { PosterConfig, PosterPurpose } from '@/types'
import {
  FOOTER_SIZE_MM,
  HEADLINE_SIZE_MM,
  LAYOUT_DIMS,
  LOGO_SIZES_MM,
  PADDING_MM,
  QR_SIZE_MM,
  SUBHEAD_SIZE_MM,
  getPosterTheme,
  posterQrStyle,
} from '@/components/admin/marketing/posterThemes'
import {
  buildPosterCss,
  buildPosterHtml,
  type PosterBrand,
} from '@/components/admin/marketing/PosterCanvas'

// PNG export — pure Canvas 2D, no SVG, no <foreignObject>.
//
// The previous SVG/foreignObject approach hit Chromium's longstanding
// security rule that any SVG containing a foreignObject taints the
// canvas (crbug.com/294129, intentionally WontFix), which then makes
// `canvas.toBlob()` throw a SecurityError. To dodge that entirely we
// paint the poster directly with the Canvas 2D API. This means we own
// the layout math here — the shape mirrors the live preview's flexbox
// layout, top to bottom, with the same per-layout sizes from
// posterThemes.ts so PNG output matches what the admin sees.
export async function exportPosterPng({
  config,
  brand,
  purpose: _purpose,
  filename,
}: {
  config: PosterConfig
  brand: PosterBrand
  purpose: PosterPurpose
  filename: string
}) {
  const theme = getPosterTheme(config.themeId)
  const dims = LAYOUT_DIMS[config.layout]
  const padding = PADDING_MM[config.layout]
  const logoMm = LOGO_SIZES_MM[config.layout][config.logoSize]
  const qrMm = QR_SIZE_MM[config.layout]
  const headlineMm = HEADLINE_SIZE_MM[config.layout]
  const subheadMm = SUBHEAD_SIZE_MM[config.layout]
  const footerMm = FOOTER_SIZE_MM[config.layout]

  const accentTrim = (config.accentOverride || '').trim()
  const accent = /^#[0-9a-f]{6}$/i.test(accentTrim) ? accentTrim : theme.accent

  // Render at 3× device-independent density (≈300 DPI for A4). One
  // logical millimetre = 96/25.4 ≈ 3.7795 CSS pixels; multiplying by 3
  // hits ~283 dpi which is sharp enough to print and small enough to
  // ship over email without a chunky JPG-grade re-compression.
  const SCALE = (96 / 25.4) * 3
  const mm = (v: number) => v * SCALE
  const W = Math.round(mm(dims.wMm))
  const H = Math.round(mm(dims.hMm))

  // Wait for fonts loaded by the parent app so headline serif + body
  // sans render correctly instead of falling back to Times. The whole
  // app uses Instrument Serif + Inter from index.html, so this is
  // effectively just "is the page font-ready" check.
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready
    } catch {
      /* non-fatal */
    }
  }

  // Pre-load QR + logo asynchronously. QR is always available (we
  // render it in-memory). Logo only loads when the user has set one
  // AND the host serves CORS headers OR is same-origin.
  // Same logo the poster's own header draws, resolved to a data URL so the QR
  // canvas is never tainted (see brandedQr.ts). Null ⇒ plain QR.
  const qrLogo = brand.brandedQr === false ? null : await loadBrandLogo(brand.logoUrl)
  const qrImg = await renderQrToImage(config, theme, accent, mm(qrMm), qrLogo)
  const logoImg = brand.logoUrl ? await tryLoadImage(brand.logoUrl) : null

  // Canvas at full resolution.
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.textBaseline = 'top'

  // ── 1. Background ────────────────────────────────────────────────
  paintBackground(ctx, theme.bg, accent, W, H)

  // ── 2. Layout cursor ─────────────────────────────────────────────
  // Centred column on A4 / tent layouts; left column on card.
  const isCard = config.layout === 'card'
  const padPx = mm(padding)
  const innerW = W - padPx * 2
  // For card layout, the QR sits in a right-side column and the text
  // flows on the left. For A4 / tent, everything stacks vertically and
  // centres horizontally.
  const colW = isCard ? innerW * 0.55 : innerW
  const colCenterX = isCard ? padPx + colW / 2 : W / 2

  // Place all draw calls in order so the y cursor stays linear.
  let y = padPx

  // ── 3. Brand block (logo + business name) ───────────────────────
  const logoSizePx = mm(logoMm)
  if (logoImg) {
    drawCircleImage(
      ctx,
      logoImg,
      colCenterX,
      y + logoSizePx / 2,
      logoSizePx,
      theme.surface,
      theme.border,
    )
  } else {
    drawMonogram(
      ctx,
      brand.businessName,
      colCenterX,
      y + logoSizePx / 2,
      logoSizePx,
      theme,
    )
  }
  y += logoSizePx + mm(4)

  ctx.font = `600 ${mm(isCard ? 4 : 5)}px ${quoteFontStack(theme.sansFont)}`
  ctx.fillStyle = theme.inkMuted
  ctx.textAlign = isCard ? 'left' : 'center'
  drawTextCentered(
    ctx,
    (brand.businessName || 'Your business').toUpperCase(),
    isCard ? padPx : colCenterX,
    y,
    { letterSpacingEm: 0.04 },
  )
  y += mm(isCard ? 6 : 8)

  // ── 4. Headline ─────────────────────────────────────────────────
  const headlinePx = mm(headlineMm)
  ctx.fillStyle = theme.ink
  ctx.font = theme.decorations.hardEdges
    ? `800 ${headlinePx}px ${quoteFontStack(theme.sansFont)}`
    : `400 ${headlinePx}px ${quoteFontStack(theme.serifFont)}`
  ctx.textAlign = isCard ? 'left' : 'center'
  const headlineText = config.headline || ''
  const headlineMax = isCard ? colW : innerW * 0.85
  const headlineLineH = headlinePx * theme.headlineLeading
  const headlineLines = wrapText(
    ctx,
    theme.decorations.hardEdges ? headlineText.toUpperCase() : headlineText,
    headlineMax,
  )
  for (const line of headlineLines) {
    ctx.fillText(line, isCard ? padPx : colCenterX, y)
    y += headlineLineH
  }
  y += mm(3)

  // ── 5. Subhead ──────────────────────────────────────────────────
  const subheadPx = mm(subheadMm)
  ctx.fillStyle = theme.inkMuted
  ctx.font = `400 ${subheadPx}px ${quoteFontStack(theme.sansFont)}`
  ctx.textAlign = isCard ? 'left' : 'center'
  const subheadMax = isCard ? colW : innerW * 0.7
  const subheadLines = wrapText(ctx, config.subhead || '', subheadMax)
  const subheadLineH = subheadPx * 1.35
  for (const line of subheadLines) {
    ctx.fillText(line, isCard ? padPx : colCenterX, y)
    y += subheadLineH
  }
  if (subheadLines.length) y += mm(5)

  // ── 6. QR card (rounded surface + QR PNG + caption) ─────────────
  const qrPx = mm(qrMm)
  const qrCardPad = mm(4)
  const qrCaptionPx = mm(3.5)
  const qrCaptionGap = mm(2.5)
  const hasCaption = !!config.qrCaption.trim()
  const captionH = hasCaption ? qrCaptionGap + qrCaptionPx * 1.2 : 0
  const cardW = qrPx + qrCardPad * 2
  const cardH = qrPx + qrCardPad * 2 + captionH
  let cardX: number
  let cardY: number
  if (isCard) {
    // Card layout: QR sits in the right column, vertically centred.
    cardX = W - padPx - cardW
    cardY = (H - cardH) / 2
  } else {
    cardX = (W - cardW) / 2
    cardY = y
  }
  drawQrCard(ctx, {
    x: cardX,
    y: cardY,
    w: cardW,
    h: cardH,
    qrImg,
    qrPx,
    cardPad: qrCardPad,
    surface: theme.surface,
    border: theme.border,
    accent,
    surfaceInk: theme.surfaceInk,
    caption: config.qrCaption,
    captionPx: qrCaptionPx,
    captionGap: qrCaptionGap,
    sansFont: theme.sansFont,
    hardEdges: !!theme.decorations.hardEdges,
  })
  if (!isCard) y = cardY + cardH + mm(5)

  // ── 7. Explainer 1-2-3 ──────────────────────────────────────────
  if (config.showExplainer && !isCard) {
    const stepFontPx = mm(4)
    ctx.font = `500 ${stepFontPx}px ${quoteFontStack(theme.sansFont)}`
    ctx.textAlign = 'left'
    const steps = ['Scan the code', 'Enter your phone', 'Start earning']
    // Measure total width so we can centre the row.
    const gap = mm(5)
    const numSize = mm(6)
    const itemWidths = steps.map((s, i) => {
      const w = ctx.measureText(s).width
      return numSize + mm(2) + w + (i < steps.length - 1 ? gap : 0)
    })
    const rowW = itemWidths.reduce((a, b) => a + b, 0)
    let cursor = (W - rowW) / 2
    steps.forEach((label, i) => {
      // Number ring
      ctx.lineWidth = mm(0.3)
      ctx.strokeStyle = accent
      ctx.beginPath()
      ctx.arc(cursor + numSize / 2, y + numSize / 2, numSize / 2, 0, Math.PI * 2)
      ctx.stroke()
      ctx.fillStyle = accent
      ctx.font = `italic 400 ${mm(4.2)}px ${quoteFontStack(theme.serifFont)}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(String(i + 1), cursor + numSize / 2, y + numSize / 2)
      // Label
      ctx.fillStyle = theme.ink
      ctx.font = `500 ${stepFontPx}px ${quoteFontStack(theme.sansFont)}`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillText(label, cursor + numSize + mm(2), y + numSize / 2)
      cursor += itemWidths[i] ?? 0
    })
    ctx.textBaseline = 'top'
    y += numSize + mm(6)
  }

  // ── 8. Footer ───────────────────────────────────────────────────
  if (config.footer.trim()) {
    const footerPx = mm(footerMm)
    ctx.fillStyle = theme.inkMuted
    ctx.font = `400 ${footerPx}px ${quoteFontStack(theme.sansFont)}`
    ctx.textAlign = isCard ? 'left' : 'center'
    const footerY = H - padPx - footerPx * 1.2
    ctx.fillText(
      config.footer,
      isCard ? padPx : W / 2,
      Math.max(y, footerY),
    )
  }

  // ── Export ──────────────────────────────────────────────────────
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas produced no blob'))),
      'image/png',
    ),
  )
  downloadBlob(blob, filename)
}

// ── Painting helpers ───────────────────────────────────────────────

function paintBackground(
  ctx: CanvasRenderingContext2D,
  bg: string,
  accent: string,
  W: number,
  H: number,
) {
  // Always start from a known solid base so the canvas isn't
  // transparent if gradient parsing falls back.
  const solidMatch = bg.match(/^#[0-9a-f]{3,8}$/i)
  if (solidMatch) {
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, H)
    return
  }

  // For multi-stop gradient themes we paint:
  //   a) a sensible solid base colour we extract from the gradient
  //   b) one or two soft radial highlights using `accent` so the
  //      poster still feels alive without needing a full CSS parser.
  // This matches the on-screen preview's *feel* rather than its exact
  // pixels, which is the right trade-off for printed output.
  const baseHex =
    bg.match(/#([0-9a-f]{6})/i)?.[0] ||
    bg.match(/rgba?\([^)]+\)/i)?.[0] ||
    '#fdf8f1'
  ctx.fillStyle = baseHex
  ctx.fillRect(0, 0, W, H)

  // Determine if the bg implies a dark theme — pick a contrasting
  // glow blend mode if so.
  const isDark = isLikelyDark(baseHex)
  ctx.save()
  ctx.globalCompositeOperation = isDark ? 'screen' : 'multiply'
  const g1 = ctx.createRadialGradient(
    W * 0.2,
    H * 0.15,
    0,
    W * 0.2,
    H * 0.15,
    Math.max(W, H) * 0.55,
  )
  g1.addColorStop(0, hexWithAlpha(accent, isDark ? 0.45 : 0.14))
  g1.addColorStop(1, hexWithAlpha(accent, 0))
  ctx.fillStyle = g1
  ctx.fillRect(0, 0, W, H)
  const g2 = ctx.createRadialGradient(
    W * 0.85,
    H * 0.85,
    0,
    W * 0.85,
    H * 0.85,
    Math.max(W, H) * 0.6,
  )
  g2.addColorStop(0, hexWithAlpha(accent, isDark ? 0.28 : 0.08))
  g2.addColorStop(1, hexWithAlpha(accent, 0))
  ctx.fillStyle = g2
  ctx.fillRect(0, 0, W, H)
  ctx.restore()
}

function drawCircleImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  cx: number,
  cy: number,
  size: number,
  surface: string,
  border: string,
) {
  const r = size / 2
  // Surface plate behind the logo (matches the CSS card)
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = surface
  ctx.fill()
  ctx.strokeStyle = border
  ctx.lineWidth = Math.max(1, size * 0.012)
  ctx.stroke()
  // Clip to circle and draw the logo with object-fit: contain math
  ctx.clip()
  const pad = size * 0.08
  const innerSize = size - pad * 2
  const ratio = img.naturalWidth / img.naturalHeight || 1
  let dw = innerSize
  let dh = innerSize
  if (ratio > 1) dh = innerSize / ratio
  else dw = innerSize * ratio
  ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh)
  ctx.restore()
}

function drawMonogram(
  ctx: CanvasRenderingContext2D,
  name: string,
  cx: number,
  cy: number,
  size: number,
  theme: ReturnType<typeof getPosterTheme>,
) {
  const r = size / 2
  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = theme.surface
  ctx.fill()
  ctx.strokeStyle = theme.border
  ctx.lineWidth = Math.max(1, size * 0.012)
  ctx.stroke()
  ctx.fillStyle = theme.surfaceInk
  const letters = (name || 'B')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
  ctx.font = `400 ${size * 0.45}px ${quoteFontStack(theme.serifFont)}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(letters || 'B', cx, cy)
  ctx.restore()
}

interface QrCardArgs {
  x: number
  y: number
  w: number
  h: number
  qrImg: HTMLImageElement
  qrPx: number
  cardPad: number
  surface: string
  border: string
  accent: string
  surfaceInk: string
  caption: string
  captionPx: number
  captionGap: number
  sansFont: string
  hardEdges: boolean
}
function drawQrCard(ctx: CanvasRenderingContext2D, a: QrCardArgs) {
  // Surface card
  const r = a.hardEdges ? 0 : Math.min(a.w, a.h) * 0.05
  roundedRect(ctx, a.x, a.y, a.w, a.h, r)
  ctx.fillStyle = a.surface
  ctx.fill()
  ctx.lineWidth = Math.max(1, a.qrPx * 0.005)
  ctx.strokeStyle = a.border
  ctx.stroke()
  if (!a.hardEdges) {
    // Soft inner highlight matches the inset 0.3mm white CSS rule
    roundedRect(ctx, a.x + 1, a.y + 1, a.w - 2, a.h - 2, Math.max(0, r - 1))
    ctx.strokeStyle = 'rgba(255,255,255,0.6)'
    ctx.lineWidth = 1
    ctx.stroke()
    // Accent inner border
    const inset = a.cardPad * 0.25
    roundedRect(
      ctx,
      a.x + inset,
      a.y + inset,
      a.w - inset * 2,
      a.h - inset * 2,
      Math.max(0, r - inset),
    )
    ctx.strokeStyle = hexWithAlpha(a.accent, 0.33)
    ctx.lineWidth = Math.max(1, a.qrPx * 0.004)
    ctx.stroke()
  }

  // QR image
  ctx.drawImage(
    a.qrImg,
    a.x + a.cardPad,
    a.y + a.cardPad,
    a.qrPx,
    a.qrPx,
  )

  // Caption
  if (a.caption.trim()) {
    ctx.fillStyle = a.surfaceInk
    ctx.font = `600 ${a.captionPx}px ${quoteFontStack(a.sansFont)}`
    ctx.textAlign = 'center'
    ctx.fillText(
      a.caption,
      a.x + a.w / 2,
      a.y + a.cardPad + a.qrPx + a.captionGap,
    )
  }
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

// Manually wrap text — Canvas 2D has no built-in line breaking.
// Conservative: greedy word-fill, no hyphenation. Empty strings yield
// an empty array so the caller can no-op.
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const trimmed = String(text ?? '').trim()
  if (!trimmed) return []
  const words = trimmed.split(/\s+/)
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const test = line ? `${line} ${word}` : word
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line)
      line = word
    } else {
      line = test
    }
  }
  if (line) lines.push(line)
  return lines
}

// fillText supports neither letter-spacing nor multi-line, so when we
// need a tracked-out caps label (the BUSINESS NAME pill above the
// headline) we paint each char manually with extra advance.
function drawTextCentered(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  opts: { letterSpacingEm: number },
) {
  if (!opts.letterSpacingEm) {
    ctx.fillText(text, x, y)
    return
  }
  const fontSize = parseFloat(ctx.font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '12')
  const spacingPx = fontSize * opts.letterSpacingEm
  const chars = [...text]
  const widths = chars.map((c) => ctx.measureText(c).width)
  const total = widths.reduce((a, b) => a + b, 0) + spacingPx * (chars.length - 1)
  let cursor =
    ctx.textAlign === 'center'
      ? x - total / 2
      : ctx.textAlign === 'right'
        ? x - total
        : x
  ctx.textAlign = 'left'
  for (let i = 0; i < chars.length; i++) {
    ctx.fillText(chars[i] ?? '', cursor, y)
    cursor += (widths[i] ?? 0) + spacingPx
  }
}

// ── Asset loaders ──────────────────────────────────────────────────

async function renderQrToImage(
  config: PosterConfig,
  theme: ReturnType<typeof getPosterTheme>,
  accent: string,
  sizePx: number,
  logoDataUrl: string | null,
): Promise<HTMLImageElement> {
  const qr = buildBrandedQr({
    data: (config.qrTargetUrl || '').trim() || 'https://example.com',
    size: Math.max(64, Math.round(sizePx)),
    style: posterQrStyle(theme.surfaceInk, theme.surface, accent),
    logoDataUrl,
    logoEnabled: Boolean(logoDataUrl),
    logoScale: QR_LOGO_SCALE_DEFAULT,
  })
  const blob = (await qr.getRawData('png')) as Blob | null
  if (!blob) throw new Error('QR rendering returned no data')
  const url = URL.createObjectURL(blob)
  try {
    return await loadImage(url)
  } finally {
    URL.revokeObjectURL(url)
  }
}

// Try to load an external logo into an image SAFELY for canvas use.
// Two attempts:
//   1. Fetch as Blob and load via Object URL — always CORS-clean if
//      the host returns the body at all, because the fetched bytes
//      are now same-origin from the canvas's point of view.
//   2. If fetch fails (network / CORS), fall back to null (caller
//      shows the monogram). Loading directly without the fetch path
//      would taint the canvas on cross-origin hosts that don't send
//      Access-Control-Allow-Origin.
async function tryLoadImage(url: string): Promise<HTMLImageElement | null> {
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) return null
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    try {
      return await loadImage(objectUrl)
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  } catch {
    return null
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    img.src = src
  })
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ── Tiny colour helpers ───────────────────────────────────────────

function hexWithAlpha(hex: string, alpha: number): string {
  // Accept both #rgb and #rrggbb. Returns rgba() so it composes with
  // any existing fillStyle / globalAlpha cleanly.
  const m = hex.match(/^#([0-9a-f]{3,8})$/i)
  if (!m || !m[1]) return `rgba(255,170,80,${alpha})`
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (h.length < 6) h = h.padEnd(6, '0')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function isLikelyDark(hex: string): boolean {
  const m = hex.match(/^#([0-9a-f]{6})$/i)
  if (!m || !m[1]) return false
  const h = m[1]
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  // Rec. 601 luminance — fast and good enough for the dark/light
  // gradient-glow decision.
  return 0.299 * r + 0.587 * g + 0.114 * b < 100
}

// The theme stores font stacks as quoted CSS strings (e.g. `"Instrument
// Serif", "Iowan Old Style", Georgia, serif`). Canvas' `font` shorthand
// accepts the same syntax verbatim — we just trim outer whitespace.
function quoteFontStack(stack: string): string {
  return stack.trim()
}

// ── Print flow ───────────────────────────────────────────────────
//
// Uses a hidden, off-screen iframe rather than `window.open` for three
// reasons:
//
//   1. `window.open` is reliably popup-blocked. Modern Chromium also
//      returns `null` from `window.open(url, name, 'noopener,…')` —
//      the older code attempted `w.document.write` on that null and
//      silently failed.
//   2. Iframes always succeed because they don't trigger the
//      browser's popup-blocking heuristics.
//   3. We get a same-origin handle to `contentWindow`, so we can fire
//      `print()` from the parent once the load event fires — no
//      reliance on inline scripts inside the print document.
//
// The iframe is positioned off-screen at (-10000,-10000) so it never
// shows up in the layout, then removed on the `afterprint` event (or
// after a generous safety timeout if `afterprint` never fires).
export async function openPosterPrintWindow({
  config,
  brand,
  purpose,
}: {
  config: PosterConfig
  brand: PosterBrand
  purpose: PosterPurpose
}) {
  const theme = getPosterTheme(config.themeId)
  const dims = LAYOUT_DIMS[config.layout]
  const padding = PADDING_MM[config.layout]
  const logoMm = LOGO_SIZES_MM[config.layout][config.logoSize]
  const qrMm = QR_SIZE_MM[config.layout]
  const headlineMm = HEADLINE_SIZE_MM[config.layout]
  const subheadMm = SUBHEAD_SIZE_MM[config.layout]
  const footerMm = FOOTER_SIZE_MM[config.layout]

  const accentTrim = (config.accentOverride || '').trim()
  const accent = /^#[0-9a-f]{6}$/i.test(accentTrim) ? accentTrim : theme.accent
  const scope = 'poster-print'

  let posterHtml = buildPosterHtml({
    config,
    brand,
    purpose,
    theme,
    accent,
    dims,
    padding,
    logoMm,
    qrMm,
    headlineMm,
    subheadMm,
    footerMm,
    forPrint: true,
  })
  const posterCss = buildPosterCss(scope, theme, accent, config.layout)
  const pageRule =
    config.layout === 'a4'
      ? '@page { size: A4 portrait; margin: 0; }'
      : config.layout === 'tent'
        ? '@page { size: A5 portrait; margin: 0; }'
        : `@page { size: ${dims.wMm}mm ${dims.hMm}mm; margin: 0; }`

  const qrDataUrl = await renderQrDataUrl(
    config,
    theme,
    accent,
    brand.brandedQr === false ? null : await loadBrandLogo(brand.logoUrl),
  )
  posterHtml = posterHtml.replace(
    /(<div\s+class="qr-mount"\s+data-qr-mount[^>]*>)<\/div>/,
    `$1<img src="${qrDataUrl}" alt="QR" style="width:100%;height:100%;display:block;" /></div>`,
  )

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Poster — ${escape(brand.businessName || purpose)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet" />
<style>
  ${pageRule}
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { display: block; }
  .${scope}-wrap { width: ${dims.wMm}mm; height: ${dims.hMm}mm; }
  ${posterCss}
</style>
</head>
<body>
  <div class="${scope}-wrap ${scope}">
    ${posterHtml}
  </div>
</body>
</html>`

  await printHtmlViaIframe(html)
}

// Inject the supplied HTML into a same-origin hidden iframe and trigger
// the browser's print dialog. Cleans the iframe up on `afterprint`,
// with a 60s safety timer in case the browser never fires it (e.g.
// the user closes the dialog mid-render in Safari).
async function printHtmlViaIframe(html: string) {
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  // Off-screen + zero size so it never paints anything visible to the
  // user even on a fast machine. (visibility:hidden / display:none
  // both prevent print() from working in some browsers.)
  iframe.style.position = 'fixed'
  iframe.style.left = '-10000px'
  iframe.style.top = '-10000px'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute(
    'sandbox',
    'allow-same-origin allow-modals allow-scripts',
  )

  document.body.appendChild(iframe)

  const cleanup = () => {
    try {
      iframe.parentNode?.removeChild(iframe)
    } catch {
      /* already gone */
    }
  }

  // Use srcdoc so the iframe is same-origin and inherits document.cookie
  // / fonts / network from this origin. Falling back to a Blob URL
  // would cross-origin the contentWindow and block us from calling
  // .print() on it programmatically.
  iframe.srcdoc = html

  // Wait for the iframe to load, then give layout + webfonts an extra
  // beat to settle before firing the print dialog. Webfont loading
  // inside an iframe runs independently of the parent's font cache.
  await new Promise<void>((resolve) => {
    const done = () => resolve()
    iframe.addEventListener('load', done, { once: true })
    // Safety timeout — Safari has been known to skip the load event
    // for srcdoc in rare cases.
    setTimeout(done, 4000)
  })

  // Wait for the iframe's fonts before printing — otherwise the print
  // dialog snapshots the document with fallback fonts and we ship a
  // poster set in Times New Roman.
  const cw = iframe.contentWindow
  const cd = iframe.contentDocument
  if (cd?.fonts?.ready) {
    try {
      await cd.fonts.ready
    } catch {
      /* non-fatal */
    }
  }
  // Plus one rAF tick so the post-font-load reflow finishes before
  // we snapshot the page.
  await new Promise((r) => requestAnimationFrame(() => r(null)))

  // Remove the iframe once the print dialog closes. Both Chrome and
  // Safari fire `afterprint` on the iframe's window. We also set a
  // generous safety timeout because some embedded contexts swallow
  // afterprint entirely.
  const wnd = cw as Window | null
  if (wnd) {
    wnd.addEventListener('afterprint', cleanup, { once: true })
  }
  setTimeout(cleanup, 60_000)

  try {
    if (wnd) {
      wnd.focus()
      wnd.print()
    } else {
      // Highly unusual — no contentWindow. Bail and clean up.
      cleanup()
      throw new Error('Print iframe has no content window')
    }
  } catch (err) {
    cleanup()
    throw err
  }
}

async function renderQrDataUrl(
  config: PosterConfig,
  theme: ReturnType<typeof getPosterTheme>,
  accent: string,
  logoDataUrl: string | null,
): Promise<string> {
  const qrMm = QR_SIZE_MM[config.layout]
  const sizePx = Math.round(qrMm * 11.81)
  const url = (config.qrTargetUrl || '').trim() || 'https://example.com'
  const qr = buildBrandedQr({
    data: url,
    size: sizePx,
    style: posterQrStyle(theme.surfaceInk, theme.surface, accent),
    logoDataUrl,
    logoEnabled: Boolean(logoDataUrl),
    logoScale: QR_LOGO_SCALE_DEFAULT,
  })
  const blob = await qr.getRawData('png')
  if (!blob) return ''
  return await blobToDataUrl(blob as Blob)
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

function escape(s: string) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
